import { Injectable, OnDestroy, inject, signal, computed } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subscription, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import UserUtil, { formatUserName } from 'app/core/utilities/user-util';
import { Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import Conversation from 'entity/Conversation';
import User from 'entity/User';
import { SocialState, Convo, SocialEventType, ConvoPhase } from 'messages/websocket.messages';

import ModalActionType from '../modal-social-event/ModalActionType';
import SocialStateHelper from '../social-state-machine/social-state-helper';
import SocialStateMachine from '../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../state/app';

import { ModalSocialEventService } from './modal-social-event.service';
import { MultimediaRoomService } from './multimedia-room.service';
import { SceneService } from './scene.service';
import { ToasterVideoService } from './toaster-video.service';

@Injectable({
  providedIn: 'root',
})
export class ConvoParticipantService extends Loggable implements OnDestroy {
  private modalSocialEventService = inject(ModalSocialEventService);
  private sceneService = inject(SceneService);
  private toasterVideoService = inject(ToasterVideoService);
  private multimediaRoomService = inject(MultimediaRoomService);
  private store = inject(Store);

  public readonly isStarting = signal(false);
  public readonly isInProgress = signal(false);
  public readonly convoHostId = signal<string | undefined>(undefined);
  public toastingMaxVolume = 20;

  private readonly conversationInProgress = signal<Conversation | undefined>(undefined);
  private endedToastTimeout?: Timeout;

  public readonly user = signal<User | undefined>(undefined);
  private readonly isHost = signal(false);

  public readonly isConvoActive = computed(() => this.isStarting() || this.isInProgress());
  public readonly hostName = computed(() => {
    const hostId = this.convoHostId();
    if (!hostId) {
      return '';
    }

    const presenter = this.sceneService?.sceneManager?.getPlayer(hostId);
    if (!presenter) {
      return '';
    }

    return UserUtil.trimName(20, presenter.user.firstName || '');
  });

  private socialStateSubscription: Subscription;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('Convo participant service');

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => this.isHost.set(isHost));

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => this.user.set(user));

    this.socialStateSubscription = SocialStateMachine.shared.stateChanged$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: async (res) => {
        if (!res) {
          return;
        }

        // End any convo logic in progress if the state tells us there's no convo anymore
        if (!this.isConvoHappeningCheck(res)) {
          // Check to make sure the service doesn't think an event is in progress that isn't, and update accordingly.
          if (this.isStarting() || this.isInProgress() || this.conversationInProgress()) {
            this.handleConvoEnded();
          }

          return;
        }

        const convo = res.socialEvent as Convo;
        const currentUser = this.user();
        const userIsPresenter = currentUser?.userId === convo.presenter.userId;

        // Presenter logic is handled in convo.service.ts!
        if (userIsPresenter) {
          return;
        }

        if (convo.phase === ConvoPhase.ANNOUNCE_PHASE) {
          if (!this.isStarting()) {
            await this.handleConvoDidStart(convo);
          }
        } else if (convo.phase === ConvoPhase.BANNER_PHASE) {
          if (!this.isInProgress() || !this.conversationInProgress()) {
            this.handleConvoInProgress(convo);
          }
        } else {
          this.error('Convo has no phase!');
        }
      },
    });
  }

  private isConvoHappeningCheck(providedState?: SocialState, failLogText?: string): boolean {
    const state = providedState || SocialStateMachine.shared.state;
    return !!state && SocialStateHelper.isEventHappening(state, SocialEventType.CONVO, failLogText);
  }

  private async handleConvoDidStart(convo: Convo): Promise<void> {
    // Validation for CleanUpTask.Service
    if (
      this.sceneService &&
      this.sceneService.sceneManager &&
      this.sceneService?.sceneManager?.player &&
      this.sceneService?.sceneManager?.player.userId === convo.presenter.userId
    ) {
      return;
    }

    this.convoHostId.set(convo.presenter.userId);
    // Set Flags
    this.isStarting.set(true);
    this.isInProgress.set(false);

    const toaster = await this.sceneService?.sceneManager?.getPlayerAsync(convo.presenter.userId);

    // Add Video from the relevant area (top-right corner originally)
    const hostId = this.convoHostId();
    if (hostId && toaster) {
      await this.toasterVideoService.addToaster(hostId);
    }
    const firstName = convo.presenter.firstName || '';
    const name = UserUtil.trimName(20, firstName);
    // only hosts should see this modal
    if (this.isHost()) {
      this.modalSocialEventService
        .open({
          title: `Ice Breaker Introduction`,
          message: `
        ${name} is about to start an ice breaker. When it begins, the following question will appear in the room:`,
          messageBold: convo.conversation.title ?? '',
          showContent: !!this.isHost(),
          cancelButtonTitle: 'Dismiss',
          warningMessage: `This message is for hosts only.`,
          isPresenter: false,
        })
        .subscribe({
          next: async (val) => {
            if (val === ModalActionType.CANCEL || val === ModalActionType.PRIMARY) {
              await this.requestEndConvo();
            }
          },
        });
    }

    // Set Participants Volume
    this.multimediaRoomService.adjustSoundForToastStart();
  }

  private handleConvoInProgress(convo: Convo): void {
    // Validation for CleanUpTask.Service
    if (
      this.sceneService?.sceneManager?.player &&
      this.sceneService?.sceneManager?.player.userId === convo.presenter.userId
    ) {
      return;
    }
    if (this.isInProgress() === true) {
      return;
    }

    const { conversation, presenter } = convo;
    this.convoHostId.set(presenter.userId);
    //  2.1 Set Flags
    this.isStarting.set(false);
    this.isInProgress.set(true);
    this.conversationInProgress.set(conversation);

    // Remove Video from the relevant area (top-right corner originally)
    this.toasterVideoService.removeToaster();

    // Set Participants Volume
    this.multimediaRoomService.resetParticipantSoundForToastInProgress();

    // Closes introduction convo
    this.modalSocialEventService.close();

    const name = UserUtil.trimName(20, formatUserName(presenter));

    // show Convo
    this.modalSocialEventService
      .open({
        title: `${name} IS HOSTING AN ICE BREAKER`,
        messageBold: conversation.title ?? '',
        cancelButtonTitle: this.isHost() ? `END ${name}'s ICE BREAKER` : '',
        hideModalButtonTitle: this.isHost() ? '' : 'HIDE ICE BREAKER',
        showModalButtonTitle: this.isHost() ? '' : 'SHOW ICE BREAKER',
        showContent: !!this.isHost(),
        isPresenter: false,
      })
      .subscribe({
        next: async (val) => {
          if (val === ModalActionType.CANCEL || val === ModalActionType.PRIMARY) {
            await this.requestEndConvo();
          }
        },
      });
  }

  private async requestEndConvo() {
    const currentUser = this.user();
    await SocialStateMachine.shared
      .requestEndEvent({
        prevEventType: SocialEventType.CONVO,
        userId: currentUser?.userId ?? '',
      })
      .catch((e) => this.error('Error request end convo', e));
  }

  private handleConvoEnded(): void {
    if (!this.isInProgress() && !this.isStarting()) {
      return;
    }
    this.convoHostId.set(undefined);
    this.modalSocialEventService.close();

    //  Set Flags
    this.isStarting.set(false);
    this.isInProgress.set(false);
    this.conversationInProgress.set(undefined);

    // Close the Convo
    this.modalSocialEventService.close();

    // Remove toaster and reset sound
    this.toasterVideoService.removeToaster();

    // Reset Sound
    this.multimediaRoomService.resetSoundForToastEnd();

    // end the timeout messages
    if (this.endedToastTimeout) {
      clearTimeout(this.endedToastTimeout);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    if (this.socialStateSubscription) {
      this.socialStateSubscription.unsubscribe();
    }
  }
}
